import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { SpecifiedContentCover } from 'scenes/home/types';

import THEME from 'theme';
import {
  Text,
  Accordion,
  AccordionItem,
  Icon,
  List,
  ListItem,
} from 'components/chroma';

import { replaceSpaces } from 'scenes/common/helpers/text';

const CONTENTS_LIMITS_WITHIN_HOME_ITEMS = [
  {
    itemDescription:
      'Hand woven carpets, oriental rugs, artwork, antiques, curios, furs, collections and collectables',
    limitDescription:
      'up to $2,000 for any one item, pair, set or collection and up to $5,000 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription:
      'Computers (including portable computers), computer equipment, electronic organisers, tablets and mobile telephones',
    limitDescription: 'up to $1,000 per item and up to $5,000 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription:
      'CDs, DVDs, videos, media purchased online, licensed computer games and software',
    limitDescription: 'up to $2,500 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription: 'Jewellery and watches',
    limitDescription: 'up to $1,000 per item and $5,000 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription:
      'Personal medical equipment, wheelchairs, artificial body parts, aids and accessories',
    limitDescription: 'up to $1,000 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription: 'Items containing gold or silver',
    limitDescription: '$1,000 per item and $5,000 in total',
    availableForLimitIncrease: true,
  },
  {
    itemDescription: 'Money and negotiable documents of any kind',
    limitDescription: 'up to $200 in total',
    availableForLimitIncrease: false,
  },
  {
    itemDescription:
      'Accessories & spare parts for motor vehicles, motorcycles, minibikes, caravan, trailer and watercraft',
    limitDescription: 'up to $500 in total',
    availableForLimitIncrease: false,
  },
  {
    itemDescription:
      'Tools, instruments and equipment used for your business or occupation',
    limitDescription: 'up to $1,000 per item and $5,000 in total',
    availableForLimitIncrease: false,
  },
  {
    itemDescription:
      'Any contents stolen from an unlocked storage room, garage or shed',
    limitDescription: 'up to $1,000 per item',
    availableForLimitIncrease: false,
  },
];

const ELEGIBLE_PORTABLE_CONTENTS_ITEMS = [
  'jewellery and watches',
  'handbags, purses and wallets, excluding their contents',
  'prescription eyeglasses, contact lenses and sunglasses',
  'binoculars, telescopes',
  'portable music systems, E-books and CB radios',
  'laptop computers, mobile phones and tablets',
  'cameras, photographic equipment, video cameras and associated accessories',
  'hearing aids, wheelchairs and mobility devices such as motorised mobility scooters',
  'baby capsules, child car seats, prams',
  'bicycles',
  'medals',
  'portable musical instruments',
  'non-fabric travel bags, suitcases and briefcases, excluding their contents',
  'picnic sets, picnic rugs and non-fabric camping equipment',
  'sporting equipment and related accessories',
  'writing instruments',
];

const Wrapper = styled.div`
  margin-top: 2rem;
`;

const Container = styled.div`
  table {
    width: 100%;
  }
  th {
    background-color: ${THEME.BACKGROUND.HEADING};
  }
  th,
  td {
    padding: 0.5rem;
    border-left: 1px solid ${THEME.FIELD.BORDER.LIGHT};
    :first-child {
      border-left: none;
    }
    :last-child {
      text-align: center;
    }
  }
`;

const ContentsLimitsWithinHome: FunctionComponent = () => (
  <Container>
    <table>
      <thead>
        <tr>
          <th scope="col" id="item_type">
            <Text weight={700} margin={{ bottom: 0 }}>
              Item type
            </Text>
          </th>
          <th scope="col" id="general_content_limit">
            <Text weight={700} margin={{ bottom: 0 }}>
              General content limit
            </Text>
          </th>
          <th scope="col" id="available_for_limit_increase">
            <Text weight={700} margin={{ bottom: 0 }}>
              Available for limit increase
            </Text>
          </th>
        </tr>
      </thead>
      {CONTENTS_LIMITS_WITHIN_HOME_ITEMS.map(
        ({ itemDescription, limitDescription, availableForLimitIncrease }) => (
          <tbody key={replaceSpaces(itemDescription)}>
            <tr>
              <td headers="item_type">{itemDescription}</td>
              <td headers="general_content_limit">{limitDescription}</td>
              <td headers="available_for_limit_increase">
                <Icon name={availableForLimitIncrease ? 'check' : 'times'} />
              </td>
            </tr>
          </tbody>
        )
      )}
    </table>
  </Container>
);

const EligiblePortableContentsItems: FunctionComponent = () => (
  <List bullet>
    {ELEGIBLE_PORTABLE_CONTENTS_ITEMS.map((item: string) => (
      <ListItem key={replaceSpaces(item)}>
        <Text>{item}</Text>
      </ListItem>
    ))}
  </List>
);

const InformationTable: FunctionComponent<{
  cover?: SpecifiedContentCover;
}> = ({ cover }) => (
  <Wrapper>
    <Accordion>
      {!cover || cover === SpecifiedContentCover.Home ? (
        <AccordionItem
          label={{ text: 'Contents limits within the home' }}
          content={{
            component: <ContentsLimitsWithinHome />,
          }}
        />
      ) : (
        <></>
      )}
      {!cover || cover === SpecifiedContentCover.PortableContents ? (
        <AccordionItem
          label={{ text: 'Eligible portable contents items' }}
          content={{
            component: <EligiblePortableContentsItems />,
          }}
        />
      ) : (
        <></>
      )}
    </Accordion>
  </Wrapper>
);

export default InformationTable;
